import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, BuildingIcon, UserIcon, CopyIcon, CheckIcon, Trash2Icon, LinkIcon, ClockIcon, UploadIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { Supplier, mockSuppliers, InvitedSupplier, mockInvitedSuppliers } from '../mocks/mockSuppliers';
export function Suppliers() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'owed' | 'invited'>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [invitedSuppliers, setInvitedSuppliers] = useState<InvitedSupplier[]>(mockInvitedSuppliers);
  const filteredSuppliers = mockSuppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) || supplier.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || activeTab === 'owed' && supplier.amountOwed !== `${getTenant().currency.code} 0.00`;
    const matchesArchived = showArchived ? true : !supplier.isArchived;
    return matchesSearch && matchesTab && matchesArchived;
  });
  const filteredInvited = invitedSuppliers.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleCopyInviteLink = (supplier: InvitedSupplier) => {
    const fullLink = `${window.location.origin}${supplier.inviteLink}`;
    navigator.clipboard.writeText(fullLink);
    setCopiedId(supplier.id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  const handleDeleteInvite = (id: string) => {
    setInvitedSuppliers(prev => prev.filter(s => s.id !== id));
  };
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-12640">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-12641">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12642">
          {t('pages.Suppliers.suppliers')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-12643">
          <button onClick={() => navigate('/supplier-bulk-upload')} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-12644">
            <UploadIcon size={16} className="mr-2" data-id="element-12645" />
            {t('pages.Suppliers.upload')}
          </button>
          <button onClick={() => navigate('/supplier-form')} className="px-4 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12646">
            {t('pages.Suppliers.add_supplier')}
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12647">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-12648">
          {/* Tabs */}
          <div className="flex space-x-6" role="tablist" aria-label="Supplier tabs" data-id="element-12649">
            <button role="tab" aria-selected={activeTab === 'all'} aria-controls="suppliers-tabpanel" id="suppliers-tab-all" onClick={() => setActiveTab('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'all' ? 'border-[#FFC70A] text-[#B38A00]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-12650">
              {t('pages.Suppliers.all')}
            </button>
            <button role="tab" aria-selected={activeTab === 'owed'} aria-controls="suppliers-tabpanel" id="suppliers-tab-owed" onClick={() => setActiveTab('owed')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'owed' ? 'border-[#FFC70A] text-[#B38A00]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-12651">
              {t('pages.Suppliers.money_owed_to')}
            </button>
            <button role="tab" aria-selected={activeTab === 'invited'} aria-controls="suppliers-tabpanel" id="suppliers-tab-invited" onClick={() => setActiveTab('invited')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'invited' ? 'border-[#FFC70A] text-[#B38A00]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-12652">
              {t('pages.Suppliers.invited')}
              {invitedSuppliers.length > 0 && <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${activeTab === 'invited' ? 'bg-[#FFC70A]/10 text-[#B38A00]' : 'bg-gray-100 text-gray-500'}`} data-id="element-12653">
                  {invitedSuppliers.length}
                </span>}
            </button>
          </div>

          {/* Right Controls */}
          <div className="flex items-center space-x-6" data-id="element-12654">
            {activeTab !== 'invited' && <label className="flex items-center cursor-pointer" data-id="element-12655">
                <span className="text-sm text-gray-600 mr-3 font-medium" data-id="element-12656">
                  {t('pages.Suppliers.show_archived_suppliers')}
                </span>
                <div className="relative" data-id="element-12657">
                  <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-12658" />

                  <div className={`block w-10 h-6 rounded-full transition-colors ${showArchived ? 'bg-[#FFC70A]' : 'bg-gray-200'}`} data-id="element-12659"></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-12660"></div>
                </div>
              </label>}

            <div className="relative w-64" data-id="element-12661">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-12662" />

              <input type="search" aria-label={t('pages.Suppliers.attrs.search_by_supplier')} placeholder={t('pages.Suppliers.attrs.search_by_supplier')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-12663" />
            </div>
          </div>
        </div>

        {/* Active Suppliers Table */}
        {activeTab !== 'invited' && <div className="overflow-x-auto" data-id="element-12664">
            <table className="w-full text-left border-collapse" data-id="element-12665">
              <thead data-id="element-12666">
                <tr className="border-b border-gray-100" data-id="element-12667">
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12668">
                    {t('pages.Suppliers.supplier_detail')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12669">
                    {t('pages.BulkSupplierUpload.type')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12670">
                    {t('pages.Suppliers.you_owe_them')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12671">
                    {t('pages.SupplierDetail.source')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-12672">
                    {t('pages.SupplierDetail.date_created')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-12673">
                {filteredSuppliers.map(supplier => <tr key={supplier.id} onClick={() => navigate(`/supplier-detail/${supplier.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-12674">
                    <td className="px-6 py-4" data-id="element-12675">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-[#B38A00] transition-colors" data-id="element-12676">
                        {supplier.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5" data-id="element-12677">
                        {supplier.email}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-12678">
                      {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-12679">
                          <BuildingIcon size={12} className="mr-1.5" data-id="element-12680" />
                          {t('pages.SupplierDetail.company')}
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-12681">
                          <UserIcon size={12} className="mr-1.5" data-id="element-12682" />
                          {t('pages.SupplierDetail.individual')}
                        </span>}
                    </td>
                    <td className="px-6 py-4" data-id="element-12683">
                      <div className="text-sm font-medium text-gray-900" data-id="element-12684">
                        {supplier.amountOwed}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-12685">
                      <div className="text-sm text-gray-500" data-id="element-12686">
                        {supplier.source}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-12687">
                      <div className="text-sm text-gray-500" data-id="element-12688">
                        {supplier.dateCreated}
                      </div>
                    </td>
                  </tr>)}
                {filteredSuppliers.length === 0 && <tr data-id="element-12689">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-12690">
                      {t('pages.Suppliers.no_suppliers_found_matching_your_criteria')}
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}

        {/* Invited Suppliers Table */}
        {activeTab === 'invited' && <div className="overflow-x-auto" data-id="element-12691">
            <table className="w-full text-left border-collapse" data-id="element-12692">
              <thead data-id="element-12693">
                <tr className="border-b border-gray-100" data-id="element-12694">
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12695">
                    {t('pages.Suppliers.supplier_name')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12696">
                    {t('pages.BulkSupplierUpload.type')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12697">
                    {t('pages.Suppliers.status')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12698">
                    {t('pages.Suppliers.invited')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-12699">
                    {t('pages.Suppliers.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-12700">
                {filteredInvited.map(supplier => <tr key={supplier.id} className="group" data-id="element-12701">
                    <td className="px-6 py-4" data-id="element-12702">
                      <div className="text-sm font-medium text-gray-900" data-id="element-12703">
                        {supplier.name}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-12704">
                      {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-12705">
                          <BuildingIcon size={12} className="mr-1.5" data-id="element-12706" />
                          {t('pages.SupplierDetail.company')}
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-12707">
                          <UserIcon size={12} className="mr-1.5" data-id="element-12708" />
                          {t('pages.SupplierDetail.individual')}
                        </span>}
                    </td>
                    <td className="px-6 py-4" data-id="element-12709">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-12710">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" data-id="element-12711"></span>
                        {t('pages.Suppliers.pending')}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-12712">
                      <div className="text-sm text-gray-900" data-id="element-12713">
                        {supplier.dateInvited}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1" data-id="element-12714">
                        <ClockIcon size={11} data-id="element-12715" />
                        {supplier.daysAgo} {t('pages.Suppliers.days_ago')}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-12716">
                      <div className="flex items-center justify-end gap-2" data-id="element-12717">
                        <button onClick={() => handleCopyInviteLink(supplier)} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-12718">
                          {copiedId === supplier.id ? <>
                              <CheckIcon size={13} className="mr-1.5 text-green-600" data-id="element-12719" />

                              {t('pages.SupplierForm.copied')}
                            </> : <>
                              <CopyIcon size={13} className="mr-1.5" data-id="element-12720" />
                              {t('pages.Suppliers.copy_link')}
                            </>}
                        </button>
                        <button onClick={() => handleDeleteInvite(supplier.id)} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200 transition-colors" data-id="element-12721">
                          <Trash2Icon size={13} className="mr-1.5" data-id="element-12722" />
                          {t('pages.Suppliers.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>)}
                {filteredInvited.length === 0 && <tr data-id="element-12723">
                    <td colSpan={5} className="px-6 py-12 text-center text-sm" data-id="element-12724">
                      <div className="flex flex-col items-center gap-3" data-id="element-12725">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-12726">
                          <LinkIcon size={20} className="text-gray-400" data-id="element-12727" />
                        </div>
                        <div data-id="element-12728">
                          <p className="text-gray-700 font-medium" data-id="element-12729">
                            {t('pages.Suppliers.no_pending_invitations')}
                          </p>
                          <p className="text-gray-500 mt-0.5" data-id="element-12730">
                            {t('pages.Suppliers.invite_a_supplier_by_clicking_add')}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}